import type { CSSProperties } from 'react'
import { getContrastTextColor, hexToRgb, isValidHex } from './colorUtils'

/**
 * 動画内の「見えるボタン」の見た目の計算（ADR-0024）。
 * 埋め込みプレイヤーと管理画面のエディタで同じ計算を使い、見た目をずらさない。
 *
 * 大きさはすべて**表示中のボタンの高さ（px）に比例**させる。位置と大きさは映像フレーム基準の比率
 * （`rect`）なので、全画面でもスマホでも、映像に対するボタンの見え方（文字の大きさ・余白・角丸）が変わらない。
 */

export type HotspotButtonVariant = 'SOLID' | 'OUTLINE' | 'GLASS'
export type HotspotButtonColor = 'WHITE' | 'BLACK' | 'BLUE' | 'RED' | 'GREEN' | 'YELLOW' | 'BRAND' | 'CUSTOM'
export type HotspotButtonShape = 'SQUARE' | 'ROUNDED' | 'PILL'
export type HotspotButtonIcon = 'NONE' | 'PLAY' | 'ARROW' | 'EXTERNAL'
export type HotspotEnterAnimation = 'NONE' | 'FADE' | 'ZOOM' | 'POP'
export type HotspotExitAnimation = 'NONE' | 'FADE'

/** プレイヤーが描くボタンの見た目。null なら透明なクリック領域（ADR-0022） */
export interface HotspotAppearance {
  variant: HotspotButtonVariant
  color: HotspotButtonColor
  /** color=CUSTOM のときの色（#RRGGBB） */
  customColor: string | null
  shape: HotspotButtonShape
  icon: HotspotButtonIcon
  enter: HotspotEnterAnimation
  exit: HotspotExitAnimation
}

export const HOTSPOT_BUTTON_VARIANTS: readonly HotspotButtonVariant[] = ['SOLID', 'OUTLINE', 'GLASS']
export const HOTSPOT_BUTTON_COLORS: readonly HotspotButtonColor[] = [
  'WHITE',
  'BLACK',
  'BLUE',
  'RED',
  'GREEN',
  'YELLOW',
  'BRAND',
  'CUSTOM',
]
export const HOTSPOT_BUTTON_SHAPES: readonly HotspotButtonShape[] = ['SQUARE', 'ROUNDED', 'PILL']
export const HOTSPOT_BUTTON_ICONS: readonly HotspotButtonIcon[] = ['NONE', 'PLAY', 'ARROW', 'EXTERNAL']
export const HOTSPOT_ENTER_ANIMATIONS: readonly HotspotEnterAnimation[] = ['NONE', 'FADE', 'ZOOM', 'POP']
export const HOTSPOT_EXIT_ANIMATIONS: readonly HotspotExitAnimation[] = ['NONE', 'FADE']

/** 見えるボタンに切り替えたときの初期値（登場・退場は瞬時） */
export const DEFAULT_HOTSPOT_APPEARANCE: HotspotAppearance = {
  variant: 'SOLID',
  color: 'WHITE',
  customColor: null,
  shape: 'ROUNDED',
  icon: 'NONE',
  enter: 'NONE',
  exit: 'NONE',
}

/** プリセット色の実色 */
export const HOTSPOT_PRESET_COLORS: Record<Exclude<HotspotButtonColor, 'BRAND' | 'CUSTOM'>, string> = {
  WHITE: '#FFFFFF',
  BLACK: '#111111',
  BLUE: '#2563EB',
  RED: '#DC2626',
  GREEN: '#16A34A',
  YELLOW: '#FACC15',
}

/** ブランドカラーが未設定のテナントで BRAND を選んだときの色 */
const BRAND_FALLBACK_COLOR = HOTSPOT_PRESET_COLORS.BLUE

/** 退場アニメーションの長さ（秒）。終了時刻のあと、この間だけ消えていく姿を見せる（押せない） */
export const HOTSPOT_EXIT_SECONDS = 0.3

const ENTER_TRANSITION: Record<Exclude<HotspotEnterAnimation, 'NONE'>, string> = {
  FADE: 'opacity 300ms ease-out',
  ZOOM: 'opacity 300ms ease-out, transform 300ms ease-out',
  // 少し行き過ぎて戻る（ポンと出る）
  POP: 'opacity 250ms ease-out, transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)',
}

const ENTER_FROM: Record<Exclude<HotspotEnterAnimation, 'NONE'>, CSSProperties> = {
  FADE: { opacity: 0 },
  ZOOM: { opacity: 0, transform: 'scale(0.6)' },
  POP: { opacity: 0, transform: 'scale(0.3)' },
}

/** 見た目の値として正しいか（壊れた値はプレイヤーで透明な領域として扱う） */
export function readHotspotAppearance(value: unknown): HotspotAppearance | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const pick = <T extends string>(options: readonly T[], key: string, fallback: T): T =>
    options.includes(raw[key] as T) ? (raw[key] as T) : fallback
  const color = pick(HOTSPOT_BUTTON_COLORS, 'color', DEFAULT_HOTSPOT_APPEARANCE.color)
  const customColor = typeof raw.customColor === 'string' && isValidHex(raw.customColor) ? raw.customColor : null
  return {
    variant: pick(HOTSPOT_BUTTON_VARIANTS, 'variant', DEFAULT_HOTSPOT_APPEARANCE.variant),
    // 自由色なのに色コードが壊れていたら白に倒す
    color: color === 'CUSTOM' && customColor === null ? 'WHITE' : color,
    customColor: color === 'CUSTOM' ? customColor : null,
    shape: pick(HOTSPOT_BUTTON_SHAPES, 'shape', DEFAULT_HOTSPOT_APPEARANCE.shape),
    icon: pick(HOTSPOT_BUTTON_ICONS, 'icon', DEFAULT_HOTSPOT_APPEARANCE.icon),
    enter: pick(HOTSPOT_ENTER_ANIMATIONS, 'enter', DEFAULT_HOTSPOT_APPEARANCE.enter),
    exit: pick(HOTSPOT_EXIT_ANIMATIONS, 'exit', DEFAULT_HOTSPOT_APPEARANCE.exit),
  }
}

/** ボタンの基準色（HEX）。BRAND はテナントの既定ボタン色（未設定なら青） */
export function resolveHotspotColor(appearance: HotspotAppearance, brandColor: string | null | undefined): string {
  if (appearance.color === 'BRAND') {
    return typeof brandColor === 'string' && isValidHex(brandColor) ? brandColor : BRAND_FALLBACK_COLOR
  }
  if (appearance.color === 'CUSTOM') {
    return appearance.customColor && isValidHex(appearance.customColor) ? appearance.customColor : '#FFFFFF'
  }
  return HOTSPOT_PRESET_COLORS[appearance.color]
}

const withAlpha = (hex: string, alpha: number): string => {
  const [r, g, b] = hexToRgb(hex) ?? [255, 255, 255]
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** ボタンの角丸（px）。エディタでボタンを囲む枠も、同じ丸みにそろえる */
export const hotspotButtonRadius = (shape: HotspotButtonShape, height: number): number => {
  if (shape === 'PILL') return height / 2
  if (shape === 'ROUNDED') return height * 0.22
  return height * 0.06
}

/** 左右の余白（px）。高さに比例させ、細長くない枠では幅の 8% を上限にする */
const hotspotPaddingX = (box: { width: number; height: number }): number => Math.min(box.height * 0.3, box.width * 0.08)

/** 枠線の太さ（px） */
const hotspotBorderWidth = (height: number): number => Math.max(height * 0.045, 1)

/** 映像幅960pxで16px。文字量やボタンの寸法によらず、同じ映像内ではサイズを揃える。 */
export function hotspotFontSize(videoWidth: number): number {
  return (videoWidth / 960) * 16
}

/**
 * ボタン本体の inline スタイル（位置は含まない）。埋め込み先の CSS に左右されないよう全部 inline で持つ。
 * `box` は表示中のボタンの大きさ（px）。
 */
export function hotspotButtonStyle(
  appearance: HotspotAppearance,
  box: { width: number; height: number },
  videoWidth: number,
  brandColor: string | null | undefined,
  isHover = false,
): CSSProperties {
  const { height } = box
  const base = resolveHotspotColor(appearance, brandColor)
  const fontSize = hotspotFontSize(videoWidth)
  const borderWidth = hotspotBorderWidth(height)
  const common: CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: `${fontSize * 0.35}px`,
    padding: `0 ${hotspotPaddingX(box)}px`,
    borderRadius: `${hotspotButtonRadius(appearance.shape, height)}px`,
    fontSize: `${fontSize}px`,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    fontFamily: 'inherit',
    whiteSpace: 'normal',
    overflow: 'hidden',
    transition: 'background-color 150ms ease-out, color 150ms ease-out, box-shadow 150ms ease-out',
  }

  if (appearance.variant === 'OUTLINE') {
    // 濃い色の枠は白地にする。黒の薄い膜だと白っぽい映像の上で灰色に見えるため。
    // 白・黄など明るい色の枠は、白地だと文字が読めないので従来どおり黒の薄い膜を敷く。
    const isDarkColor = getContrastTextColor(base) === '#FFFFFF'
    if (isDarkColor) {
      const hoverTint = withAlpha(base, 0.1)
      return {
        ...common,
        color: base,
        background: isHover ? `linear-gradient(${hoverTint}, ${hoverTint}) #FFFFFF` : '#FFFFFF',
        border: `${borderWidth}px solid ${base}`,
      }
    }
    return {
      ...common,
      color: base,
      background: isHover ? withAlpha(base, 0.18) : 'rgba(0, 0, 0, 0.18)',
      border: `${borderWidth}px solid ${base}`,
      textShadow: '0 1px 3px rgba(0, 0, 0, 0.45)',
    }
  }

  if (appearance.variant === 'GLASS') {
    return {
      ...common,
      color: '#FFFFFF',
      background: withAlpha(base, isHover ? 0.45 : 0.3),
      border: `${borderWidth}px solid ${withAlpha(base, 0.7)}`,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      textShadow: '0 1px 3px rgba(0, 0, 0, 0.45)',
    }
  }

  const textColor = getContrastTextColor(base)
  // 濃色は白へ、白・黄などの明色は濃いグレーへ反転する。
  const hoverBackground = textColor === '#FFFFFF' ? '#FFFFFF' : '#111111'
  const shadow = `0 ${height * 0.06}px ${height * 0.25}px rgba(0, 0, 0, 0.3)`
  return {
    ...common,
    color: isHover ? base : textColor,
    background: isHover ? hoverBackground : base,
    border: 'none',
    boxShadow: isHover ? `inset 0 0 0 ${borderWidth}px ${base}, ${shadow}` : shadow,
  }
}

export type HotspotPhase = 'hidden' | 'active' | 'exiting'

/**
 * その時刻の表示状態。退場アニメーションがあるボタンは、終了時刻のあと `HOTSPOT_EXIT_SECONDS` だけ
 * 「消えていく途中（押せない）」として残す。
 */
export function hotspotPhaseAt(
  hotspot: { startTime: number; endTime: number; appearance: HotspotAppearance | null },
  time: number,
): HotspotPhase {
  if (time >= hotspot.startTime && time <= hotspot.endTime) return 'active'
  const fadesOut = hotspot.appearance !== null && hotspot.appearance.exit !== 'NONE'
  if (fadesOut && time > hotspot.endTime && time <= hotspot.endTime + HOTSPOT_EXIT_SECONDS) return 'exiting'
  return 'hidden'
}

/**
 * 登場・退場アニメーションのスタイル。位置は動かさない（その場で完結させる・ADR-0023 C）。
 * `entered` は登場後の状態に入ったか（初回描画は false にして、次のフレームで true にすると動く）。
 */
export function hotspotMotionStyle(appearance: HotspotAppearance, entered: boolean, exiting: boolean): CSSProperties {
  if (exiting) {
    return { opacity: 0, transition: `opacity ${HOTSPOT_EXIT_SECONDS * 1000}ms ease-in` }
  }
  if (appearance.enter === 'NONE') return { opacity: 1 }
  const transition = ENTER_TRANSITION[appearance.enter]
  return entered
    ? { opacity: 1, transform: 'scale(1)', transition }
    : { ...ENTER_FROM[appearance.enter], transition: 'none' }
}
